
import React from 'react';
import { Star, Zap, Gamepad } from 'lucide-react';
import { useWebsiteSettings } from '@/hooks/useWebsiteSettings';

interface OptimizedHeroSectionProps {
  freshnessData?: {
    totalPlays: number;
    newGamesCount: number;
  } | null;
}

const OptimizedHeroSection: React.FC<OptimizedHeroSectionProps> = ({
  freshnessData
}) => {
  const { settings, isLoading } = useWebsiteSettings();
  
  // Use fresh settings data
  const siteName = settings?.site_name || 'TerraGamers';
  const siteDescription = settings?.site_description || 'Discover and play thousands of free online games. Action, puzzle, racing, and more!';

  if (isLoading) {
    return (
      <section className="relative w-full py-16 md:py-20">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="animate-pulse">
              <div className="w-20 h-20 md:w-24 md:h-24 bg-gray-300 rounded-3xl mx-auto mb-8"></div>
              <div className="h-16 bg-gray-300 rounded mb-6 max-w-4xl mx-auto"></div>
              <div className="h-8 bg-gray-300 rounded mb-4 max-w-2xl mx-auto"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative w-full py-16 md:py-20">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="animate-slide-up-fade">
            {/* Logo Badge */}
            <div className="mb-8 inline-flex items-center justify-center">
              <div className="relative">
                <div className="w-20 h-20 md:w-24 md:h-24 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 rounded-3xl flex items-center justify-center shadow-2xl">
                  <Zap className="w-10 h-10 md:w-12 md:h-12 text-white" fill="currentColor" />
                </div>
                <div className="absolute -top-2 -right-2 md:-top-3 md:-right-3 w-6 h-6 md:w-8 md:h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                  <Star className="w-3 h-3 md:w-4 md:h-4 text-white" />
                </div>
              </div>
            </div>

            {/* Main Title - Using dynamic site name */}
            <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black bg-gradient-to-r from-slate-900 via-blue-900 to-purple-900 bg-clip-text text-transparent mb-6 md:mb-8 leading-tight tracking-tight">
              {siteName}
            </h1>
            
            {/* Subtitle - Using dynamic site description */}
            <div className="max-w-4xl mx-auto mb-6 md:mb-8">
              <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-slate-600 font-medium leading-relaxed mb-4 md:mb-6">
                {siteDescription}
              </p>
              
              {freshnessData && (
                <div className="inline-flex items-center gap-2 md:gap-3 bg-gradient-to-r from-blue-50 to-purple-50 backdrop-blur-sm px-4 py-2 md:px-6 md:py-3 rounded-2xl border border-blue-200/50 shadow-lg">
                  <Star className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                  <span className="text-sm sm:text-base md:text-lg font-semibold text-blue-700">
                    🔥 {freshnessData.totalPlays.toLocaleString()}+ games played • {freshnessData.newGamesCount} new this week
                  </span>
                </div>
              )}
            </div>
            
            {/* Enhanced Feature Pills */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 md:mb-8 max-w-4xl mx-auto">
              {[
                { icon: Zap, text: 'Instant Play', color: 'from-emerald-500 to-teal-500', bg: 'from-emerald-50 to-teal-50' },
                { icon: Gamepad, text: '100% Free', color: 'from-blue-500 to-indigo-500', bg: 'from-blue-50 to-indigo-50' },
                { icon: Star, text: 'No Downloads', color: 'from-purple-500 to-pink-500', bg: 'from-purple-50 to-pink-50' },
                { icon: Star, text: 'Premium Quality', color: 'from-orange-500 to-red-500', bg: 'from-orange-50 to-red-50' },
              ].map((item, index) => (
                <div 
                  key={item.text}
                  className={`flex flex-col sm:flex-row items-center justify-center sm:justify-start space-y-2 sm:space-y-0 sm:space-x-3 bg-gradient-to-r ${item.bg} backdrop-blur-sm px-3 py-4 sm:px-4 sm:py-3 md:px-5 md:py-3 rounded-2xl shadow-soft border border-white/60 hover:scale-105 transition-all duration-300`}
                >
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r ${item.color} rounded-xl flex items-center justify-center shadow-md`}>
                    <item.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <span className="font-semibold text-slate-700 text-xs sm:text-sm md:text-lg text-center sm:text-left">{item.text}</span>
                </div>
              ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-3xl mx-auto">
              {[
                { number: '10K+', label: 'Games Available', icon: Gamepad },
                { number: '1M+', label: 'Happy Players', icon: Gamepad },
                { number: '50+', label: 'Categories', icon: Star },
                { number: '24/7', label: 'Available', icon: Zap },
              ].map((stat, index) => (
                <div 
                  key={stat.label}
                  className="text-center p-3 md:p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-soft border border-white/60 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-2 md:mb-3 shadow-md">
                    <stat.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  </div>
                  <div className="text-xl md:text-2xl lg:text-3xl font-black text-slate-900 mb-1">{stat.number}</div>
                  <div className="text-xs md:text-sm lg:text-base text-slate-600 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OptimizedHeroSection;
